// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getProductAdditionalInfos } from "@hardwoods/App/Store/Selectors/CustomProductDetails";
import { color } from "@hardwoods/XDL/Utilities";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    return ({
        productAdditionalInfos: getProductAdditionalInfos(state),
        catalogPageState: getCatalogPageStateByPath(state, categoryPath),
    })
};

interface OwnProps {
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsCategoryPopupStyle {
    wrapper?: InjectableCss;
    message?: TypographyProps;
    modal?: ModalPresentationProps;
    InfoIcon?: IconPresentationProps;
    InfoClickable?: ClickablePresentationProps;
}

export const noteLineStyles: ProductDetailsCategoryPopupStyle = {
    wrapper: {
        css: css`
            position: relative;
        `,
    },
    modal: {
        cssOverrides: {
            modalTitle: css`
                && {
                    display: none;
                }
            `,
        },
    },
    InfoIcon: {
        src: Info,
        size: 18,
        color: "text.inverse",
        css: css`
            width: 30px;
            height: 30px;
            display: flex;
            flex-direction: center;
            justify-content: center;
            background: ${color("common.black", 0.75)};
            position: absolute;
            top: 0;
            right: 0;
        `,
    },
};

const styles = noteLineStyles;

const ProductDetailsCategoryPopup: React.FC<Props> = ({ productAdditionalInfos: { categoryId, showPopup, popupMessage }, catalogPageState }) => {
    if (showPopup !== "True" || !popupMessage) {
        return null;
    }
    const [openPopup, setOpenPopup] = React.useState(false);
    const [categoriesPopup, setCategoriesPopup] = React.useState((getCookie("categoriesPopup") && getCookie("categoriesPopup") !== undefined && JSON.parse(getCookie("categoriesPopup") as string)) || {} as any);

    React.useEffect(() => {
        if (!openPopup && catalogPageState.value && !categoriesPopup[categoryId]) {
            setOpenPopup(true);
            setCategoriesPopup((categoriesPopup: any) => {
                categoriesPopup[categoryId] = true;
                return categoriesPopup;
            });
            setCategoriesPopupCookie();
        }
    }, []);

    const modalCloseHandler = () => {
        setOpenPopup(false);
        setCategoriesPopup((categoriesPopup: any) => {
            categoriesPopup[categoryId] = true;
            return categoriesPopup;
        });
        setCategoriesPopupCookie();
    };

    const openPopupHandler = () => {
        setOpenPopup(true);
    };

    const setCategoriesPopupCookie = () => {
        const date = new Date();
        const expires = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate());
        setCookie("categoriesPopup", JSON.stringify(categoriesPopup), { expires });
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_CategoryPopup">
            <Clickable
                {...styles.InfoClickable}
                onClick={openPopupHandler}
                data-test-selector="productDetails_showPopup"
            >
                <Icon {...styles.InfoIcon} />
            </Clickable>
            <Modal isOpen={openPopup} handleClose={modalCloseHandler} headline={null}>
                {/* eslint-disable react/no-danger */}
                <div {...styles.message} dangerouslySetInnerHTML={{ __html: popupMessage }}></div>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductDetailsCategoryPopup);
